import type InviteModel from 'garaje/models/invite';
import type { ChangeHandling } from 'garaje/utils/policy-draft';
import moment from 'moment-timezone';

/**
 * True when an invite went through a workflow,
 * meaning editing + saving will trigger a fresh workflow re-evaluation.
 */
export function canTriggerWorkflowReeval(invite: InviteModel): boolean {
  return !!invite.visitApproval?.visitWorkflowResult;
}

/**
 * VIS-17085 — predicts whether an `expectedArrivalTime` edit will SKIP re-evaluation,
 * so the client can avoid warning the user about a re-approval that won't happen.
 *
 * MUST stay in sync with workflow-task-adapter's `resolveArrivalReevalStrategy`
 * (workflow.service.ts): skip iff every changed component (date / time) is
 * skip-configured, compared in the stored UTC frame at full time precision. WTA is the
 * authority — this is only a UI hint.
 */
export function arrivalChangeSkipsReevaluation(
  before: Date | string | null | undefined,
  after: Date | string | null | undefined,
  changeHandling: ChangeHandling | undefined,
): boolean {
  if (!before || !after || !changeHandling) return false;

  const b = moment.utc(before);
  const a = moment.utc(after);
  if (!b.isValid() || !a.isValid()) return false;

  const dateChanged = !b.isSame(a, 'day');
  const timeChanged = b.format('HH:mm:ss.SSS') !== a.format('HH:mm:ss.SSS');
  if (!dateChanged && !timeChanged) return false; // no real change → don't suppress the warning

  return (
    (!dateChanged || changeHandling.arrivalDate === 'skip') && (!timeChanged || changeHandling.arrivalTime === 'skip')
  );
}
